// SignUpCTA.tsx

import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Grid,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import LocalShippingRoundedIcon from "@mui/icons-material/LocalShippingRounded";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import RocketLaunchRoundedIcon from "@mui/icons-material/RocketLaunchRounded";
import SecurityRoundedIcon from "@mui/icons-material/SecurityRounded";
import SellRoundedIcon from "@mui/icons-material/SellRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";

import { useNavigate } from "react-router-dom";
import Footer from "./Footer";
import { getLoginCookieAndSession } from "../../utils";
import { useStripeAccountStatus } from "../../hooks/useStripeAccountStatus";
import { DrawerModal } from "./DrawerModal";
import Login from "./Login";

const BRAND_ORANGE = "#e78758";
const BRAND_ORANGE_DARK = "#cf7045";
const COMMISSION_RATE = "9.5%";

const SignUpSection: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { sellRoute } = useStripeAccountStatus();

  const [isSellModalOpen, setSellModalOpen] = useState(false);

  const handleStartSellingClick = () => {
    if (getLoginCookieAndSession()) {
      navigate(sellRoute);
    } else {
      setSellModalOpen(true);
    }
  };

  const handleBrowseToys = () => {
    navigate("/latest-toys");
  };

  const closeSellModal = () => {
    setSellModalOpen(false);
  };

  return (
    <>
      <Box
        component="section"
        sx={{
          px: {
            xs: 2,
            sm: 3,
          },
          py: {
            xs: 3,
            md: 4,
          },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            position: "relative",
            maxWidth: 1200,
            mx: "auto",
            overflow: "hidden",
            borderRadius: {
              xs: "24px",
              md: "32px",
            },
            color: "#ffffff",
            background:
              "linear-gradient(135deg, #1b252d 0%, #293b46 58%, #3b5360 100%)",
            boxShadow: "0 24px 60px rgba(28, 38, 46, 0.2)",

            "&::before": {
              content: '""',
              position: "absolute",
              width: {
                xs: 300,
                md: 520,
              },
              height: {
                xs: 300,
                md: 520,
              },
              top: {
                xs: -170,
                md: -290,
              },
              right: {
                xs: -160,
                md: -170,
              },
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(231,135,88,0.72) 0%, rgba(231,135,88,0) 70%)",
              pointerEvents: "none",
            },

            "&::after": {
              content: '""',
              position: "absolute",
              inset: 0,
              opacity: 0.17,
              backgroundImage: `
                linear-gradient(
                  rgba(255,255,255,0.08) 1px,
                  transparent 1px
                ),
                linear-gradient(
                  90deg,
                  rgba(255,255,255,0.08) 1px,
                  transparent 1px
                )
              `,
              backgroundSize: "48px 48px",
              maskImage:
                "linear-gradient(to right, rgba(0,0,0,0.85), transparent)",
              pointerEvents: "none",
            },
          }}
        >
          <Grid
            container
            spacing={{
              xs: 3,
              md: 5,
            }}
            alignItems="center"
            sx={{
              position: "relative",
              zIndex: 1,
              p: {
                xs: 2.5,
                sm: 3.5,
                md: 5,
              },
            }}
          >
            <Grid item xs={12} md={7}>
              <Chip
                size="small"
                icon={<RocketLaunchRoundedIcon />}
                label="START SELLING ON FIDDLEPIDDLE"
                sx={{
                  mb: 2,
                  color: "#ffffff",
                  bgcolor: "rgba(255,255,255,0.1)",
                  border: "1px solid rgba(255,255,255,0.16)",
                  fontSize: "0.7rem",
                  fontWeight: 850,
                  letterSpacing: "0.055em",

                  "& .MuiChip-icon": {
                    color: "#ffad82",
                  },
                }}
              />

              <Typography
                component="h2"
                sx={{
                  maxWidth: 690,
                  mb: 2,
                  fontSize: {
                    xs: "2rem",
                    sm: "2.75rem",
                    md: "3.4rem",
                  },
                  lineHeight: {
                    xs: 1.08,
                    md: 1.04,
                  },
                  fontWeight: 900,
                  letterSpacing: "-0.045em",
                }}
              >
                Turn unused toys into someone’s next favorite.
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  maxWidth: 650,
                  mb: 2.75,
                  color: "rgba(255,255,255,0.75)",
                  fontSize: {
                    xs: "1rem",
                    sm: "1.08rem",
                  },
                  lineHeight: 1.7,
                }}
              >
                List toys, collectibles, and childhood favorites in minutes.
                Reach buyers looking for exactly what you have and manage every
                sale from your FiddlePiddle account.
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: {
                    xs: "center",
                    sm: "flex-start",
                  },
                  gap: 1,
                  mb: 3,
                }}
              >
                <Chip
                  size="small"
                  icon={<CheckCircleRoundedIcon />}
                  label="Free to join"
                  sx={{
                    color: "#ffffff",
                    bgcolor: "rgba(69,178,120,0.14)",
                    border: "1px solid rgba(112,225,163,0.2)",

                    "& .MuiChip-icon": {
                      color: "#79e0aa",
                    },
                  }}
                />

                <Chip
                  size="small"
                  icon={<CheckCircleRoundedIcon />}
                  label="Free to list"
                  sx={{
                    color: "#ffffff",
                    bgcolor: "rgba(69,178,120,0.14)",
                    border: "1px solid rgba(112,225,163,0.2)",

                    "& .MuiChip-icon": {
                      color: "#79e0aa",
                    },
                  }}
                />

                <Chip
                  size="small"
                  icon={<SellRoundedIcon />}
                  label={`${COMMISSION_RATE} only when sold`}
                  sx={{
                    color: "#ffffff",
                    bgcolor: "rgba(255,255,255,0.09)",
                    border: "1px solid rgba(255,255,255,0.14)",

                    "& .MuiChip-icon": {
                      color: "#ffad82",
                    },
                  }}
                />

                <Chip
                  size="small"
                  icon={<SecurityRoundedIcon />}
                  label="Secure Stripe payouts"
                  sx={{
                    color: "#ffffff",
                    bgcolor: "rgba(255,255,255,0.09)",
                    border: "1px solid rgba(255,255,255,0.14)",

                    "& .MuiChip-icon": {
                      color: "#ffad82",
                    },
                  }}
                />
              </Box>

              <Stack
                direction={{
                  xs: "column",
                  sm: "row",
                }}
                spacing={1.5}
                sx={{
                  alignItems: {
                    xs: "stretch",
                    sm: "center",
                  },
                }}
              >
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleStartSellingClick}
                  endIcon={<ArrowForwardRoundedIcon />}
                  fullWidth={isMobile}
                  sx={{
                    minHeight: 52,
                    px: 3.25,
                    borderRadius: "999px",
                    color: "#ffffff",
                    bgcolor: BRAND_ORANGE,
                    fontSize: "1rem",
                    fontWeight: 850,
                    textTransform: "none",
                    boxShadow: "0 14px 32px rgba(231,135,88,0.36)",
                    transition:
                      "transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease",

                    "&:hover": {
                      color: "#ffffff",
                      bgcolor: BRAND_ORANGE_DARK,
                      boxShadow: "0 18px 38px rgba(231,135,88,0.46)",
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  Start Selling
                </Button>

                <Button
                  variant="outlined"
                  size="large"
                  onClick={handleBrowseToys}
                  startIcon={<StorefrontRoundedIcon />}
                  fullWidth={isMobile}
                  sx={{
                    minHeight: 52,
                    px: 3,
                    borderRadius: "999px",
                    color: "#ffffff",
                    borderColor: "rgba(255,255,255,0.38)",
                    bgcolor: "rgba(255,255,255,0.06)",
                    fontSize: "1rem",
                    fontWeight: 750,
                    textTransform: "none",

                    "&:hover": {
                      color: "#ffffff",
                      borderColor: "rgba(255,255,255,0.7)",
                      bgcolor: "rgba(255,255,255,0.12)",
                    },
                  }}
                >
                  Browse Toys
                </Button>
              </Stack>

              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mt: 1.75,
                  color: "rgba(255,255,255,0.56)",
                }}
              >
                No upfront seller fees. FiddlePiddle earns only when you make a
                sale.
              </Typography>
            </Grid>

            <Grid item xs={12} md={5}>
              <Paper
                elevation={0}
                sx={{
                  p: {
                    xs: 2.25,
                    sm: 2.75,
                  },
                  borderRadius: "24px",
                  color: "#ffffff",
                  bgcolor: "rgba(255,255,255,0.1)",
                  border: "1px solid rgba(255,255,255,0.16)",
                  backdropFilter: "blur(16px)",
                  boxShadow: "0 20px 46px rgba(0,0,0,0.2)",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    mb: 2.25,
                  }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "15px",
                      color: "#ffffff",
                      background:
                        "linear-gradient(135deg, #f3a078 0%, #d96f40 100%)",
                      boxShadow: "0 10px 24px rgba(217,111,64,0.32)",
                    }}
                  >
                    <SellRoundedIcon />
                  </Box>

                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 850,
                        lineHeight: 1.25,
                      }}
                    >
                      Selling made simple
                    </Typography>

                    <Typography
                      variant="caption"
                      sx={{
                        color: "rgba(255,255,255,0.6)",
                      }}
                    >
                      From listing to payout
                    </Typography>
                  </Box>
                </Box>

                <Stack spacing={1.4}>
                  {[
                    {
                      icon: <RocketLaunchRoundedIcon />,
                      title: "Create your listing",
                      description:
                        "Add your toy, photos, price, and shipping details.",
                    },
                    {
                      icon: <LocalShippingRoundedIcon />,
                      title: "Ship after it sells",
                      description:
                        "Package the order, add tracking, and keep the buyer updated.",
                    },
                    {
                      icon: <PaymentsOutlinedIcon />,
                      title: "Receive your payout",
                      description:
                        "Payouts are securely processed through Stripe.",
                    },
                  ].map((item) => (
                    <Box
                      key={item.title}
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.25,
                        p: 1.5,
                        borderRadius: "17px",
                        bgcolor: "rgba(10,17,22,0.25)",
                        border: "1px solid rgba(255,255,255,0.08)",
                      }}
                    >
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          flexShrink: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: "11px",
                          color: "#ffad82",
                          bgcolor: "rgba(231,135,88,0.12)",

                          "& .MuiSvgIcon-root": {
                            fontSize: "1.15rem",
                          },
                        }}
                      >
                        {item.icon}
                      </Box>

                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            mb: 0.25,
                            fontWeight: 800,
                          }}
                        >
                          {item.title}
                        </Typography>

                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            color: "rgba(255,255,255,0.6)",
                            lineHeight: 1.5,
                          }}
                        >
                          {item.description}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Paper>
      </Box>

      <Box
        sx={{
          mb: 2.5,
        }}
      >
        <Footer />
      </Box>

      <DrawerModal
        isOpen={isSellModalOpen}
        onClose={closeSellModal}
        anchor="bottom"
        onOpen={() => {}}
      >
        <Login openFormFrom="Seller" />
      </DrawerModal>
    </>
  );
};

export default SignUpSection;